/**
 * Precio de plata (XAG) vía gold-api.com — mismo patrón que PlatinumPriceService.
 * Nota: el transcript solo hace Read de este archivo (creado antes de la sesión);
 * reconstruido a partir de PlatinumPriceService.js + endpoint XAG/EUR.
 */
const GOLD_API_BASE_URL = "https://api.gold-api.com";
const SILVER_SYMBOL = "XAG";

let silverPriceCache = {
  usd: null,
  eur: null,
  usdPerGram: null,
  eurPerGram: null,
  lastUpdate: 0,
  date: null,
  source: null,
};

const CACHE_DURATION = 10 * 60 * 1000;
const OUNCE_TO_GRAMS = 31.1034768;

const fetchSilverPriceUSD = async () => {
  const response = await fetch(`${GOLD_API_BASE_URL}/price/${SILVER_SYMBOL}`);
  if (!response.ok) throw new Error(`API Plata USD: HTTP ${response.status}`);
  const data = await response.json();
  if (!data?.price) throw new Error("API Plata USD: sin precio");
  return {
    price: data.price,
    currency: data.currency || "USD",
    symbol: data.symbol || SILVER_SYMBOL,
    updatedAt: data.updatedAt,
  };
};

const fetchSilverPriceEUR = async () => {
  const response = await fetch(`${GOLD_API_BASE_URL}/price/${SILVER_SYMBOL}/EUR`);
  if (!response.ok) throw new Error(`API Plata EUR: HTTP ${response.status}`);
  const data = await response.json();
  if (!data?.price) throw new Error("API Plata EUR: sin precio");
  return {
    price: data.price,
    currency: data.currency || "EUR",
    symbol: data.symbol || SILVER_SYMBOL,
    updatedAt: data.updatedAt,
  };
};

export const getSilverPrice = async (forceRefresh = false) => {
  const now = Date.now();

  if (!forceRefresh && silverPriceCache.usd) {
    const cacheAge = now - silverPriceCache.lastUpdate;
    if (cacheAge < CACHE_DURATION) {
      return {
        usd: silverPriceCache.usd,
        eur: silverPriceCache.eur,
        usdPerGram: silverPriceCache.usdPerGram,
        eurPerGram: silverPriceCache.eurPerGram,
        timestamp: new Date(silverPriceCache.lastUpdate),
        date: silverPriceCache.date,
        source: silverPriceCache.source,
        fromCache: true,
      };
    }
  }

  try {
    const [usdData, eurData] = await Promise.all([
      fetchSilverPriceUSD(),
      fetchSilverPriceEUR(),
    ]);

    const usdPrice = usdData.price;
    const eurPrice = eurData.price;
    const date = usdData.updatedAt || eurData.updatedAt;
    const usdPerGram = usdPrice / OUNCE_TO_GRAMS;
    const eurPerGram = eurPrice / OUNCE_TO_GRAMS;

    silverPriceCache = {
      usd: usdPrice,
      eur: eurPrice,
      usdPerGram,
      eurPerGram,
      lastUpdate: now,
      date,
      source: "gold-api.com",
    };

    return {
      usd: usdPrice,
      eur: eurPrice,
      usdPerGram,
      eurPerGram,
      timestamp: new Date(now),
      date,
      fromCache: false,
      source: "gold-api.com",
    };
  } catch (error) {
    console.error("Error obteniendo precio de la plata:", error);

    if (silverPriceCache.usd) {
      return {
        usd: silverPriceCache.usd,
        eur: silverPriceCache.eur,
        usdPerGram: silverPriceCache.usdPerGram,
        eurPerGram: silverPriceCache.eurPerGram,
        timestamp: new Date(silverPriceCache.lastUpdate),
        date: silverPriceCache.date,
        source: silverPriceCache.source,
        fromCache: true,
        error: error.message,
      };
    }

    throw error;
  }
};

export const getSilverPricePerGramEUR = async (forceRefresh = false) => {
  const data = await getSilverPrice(forceRefresh);
  return data.eurPerGram;
};
